import React, { useEffect, useState } from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as S from './styles';
import {
  LogListItem,
  ModalResponsive,
  Loading,
  Pagination,
  DynamicFilter,
} from '../../components';
import Details from '../../assets/buttons/details.svg';
import { useLogs } from '../../hooks';
import {
  formatTimeStampToDate,
  mapNumbersToString,
} from '../../utils/functions';
import {
  IDynamicFilterData,
  IDynamicFilterSelectOptions,
} from '../../interfaces';
import { usePagination } from '../../hooks/usePagination';

export const UsersLogs: React.FC = () => {
  const { returnAllUsersLogs, usersLogs, isLoading, metadata } = useLogs();

  const [activeUser, setActiveUser] = useState<any>(null);

  const [OpenDetailsModal, setOpenDetailsModal] = useState(false);

  const [filters, setFilters] = useState<IDynamicFilterData>({
    type: 'editado',
    term: '',
    dateStart: '2023-01-01',
    dateEnd: '2023-12-31',
  });

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: metadata.totalItems || 0,
      onPageChange: (currentPage, pageSize) => {
        returnAllUsersLogs({
          type: filters.type,
          term: filters.term,
          currentPage,
          pageSize,
          dateStart: filters.dateStart,
          dateEnd: filters.dateEnd,
        });
      },
    });

  useEffect(() => {
    returnAllUsersLogs({
      type: filters.type,
      term: filters.term,
      currentPage: 1,
      pageSize,
      dateStart: filters.dateStart,
      dateEnd: filters.dateEnd,
    });
  }, []);

  const handleOpenDetails = (elm: any) => {
    setActiveUser(elm);
    setOpenDetailsModal(true);
  };

  const handleSearch = (formData: IDynamicFilterData) => {
    setFilters(formData);
    resetPagination();
    returnAllUsersLogs({
      type: formData.type,
      term: formData.term,
      currentPage: 1,
      pageSize,
      dateStart: formData.dateStart,
      dateEnd: formData.dateEnd,
    });
  };

  const returnUserLog = (elm: any) => {
    if (!elm) return '-';

    const {
      ativo,
      usuarioEditado,
      usuarioEdicao,
      areaAtuacao,
      gruposEconomicos,
    } = elm;

    if (!ativo)
      return (
        <S.Text>
          Usuário <S.Text className="bold inline">{usuarioEditado}</S.Text> foi
          desativado por{' '}
          <S.Text className="bold inline">{usuarioEdicao}</S.Text>.
        </S.Text>
      );

    if (ativo && !areaAtuacao && !gruposEconomicos)
      return (
        <S.Text>
          Usuário <S.Text className="bold inline">{usuarioEditado}</S.Text> foi
          ativado.
        </S.Text>
      );

    const logs = [];

    if (areaAtuacao && !gruposEconomicos) {
      logs.push(
        <S.Text key="area-atuacao">
          <S.Text className="bold inline">Canal Responsável </S.Text> editado
          para{' '}
          <S.Text className="blue bold inline">
            {mapNumbersToString(areaAtuacao)}.
          </S.Text>
        </S.Text>,
      );
    }

    if (gruposEconomicos && !areaAtuacao) {
      logs.push(
        <S.Text key="grupos-economicos">
          <S.Text className="bold inline">Grupo ecônomico</S.Text> editado para{' '}
          <S.Text className="blue bold inline">
            {mapNumbersToString(gruposEconomicos)}.
          </S.Text>
        </S.Text>,
      );
    }

    if (gruposEconomicos && areaAtuacao) {
      logs.push(
        <S.Text key="grupos-economicos">
          <S.Text className="bold inline">Grupo ecônomico</S.Text> editado para{' '}
          <S.Text className="blue bold inline">
            {mapNumbersToString(gruposEconomicos)}.
          </S.Text>
          <br />
          <S.Text className="bold inline">Canal Responsável</S.Text> editado
          para{' '}
          <S.Text className="blue bold inline">
            {mapNumbersToString(areaAtuacao)}.
          </S.Text>
        </S.Text>,
      );
    }
    return logs.length > 0 ? logs : '-';
  };

  const filterSelectOptions: IDynamicFilterSelectOptions[] = [
    { value: 'editado', description: 'Usuário editado' },
    { value: 'edicao', description: 'Usuário edição' },
  ];

  return (
    <S.Container>
      <S.Content>
        <DynamicFilter
          filterSelectOptions={filterSelectOptions}
          filters={filters}
          calendar
          isLoading={isLoading}
          setFilters={setFilters}
          searchFilters={handleSearch}
        />
        <S.Tabs.TabsContainer className="table-pc">
          {!isLoading && (
            <S.Tabs.TabHeader>
              <S.Tabs.TabCell>Usuário editado</S.Tabs.TabCell>
              <S.Tabs.TabCell>Editado por</S.Tabs.TabCell>
              <S.Tabs.TabCell>Data da Edição</S.Tabs.TabCell>
              <S.Tabs.TabCell className="expanded-xl">
                Ação realizada
              </S.Tabs.TabCell>
            </S.Tabs.TabHeader>
          )}
          {usersLogs.map((elm: any) => {
            return (
              <S.Tabs.TabBody key={elm.id}>
                <S.Tabs.TabCell data-label="Usuário editado">
                  {elm.usuarioEditado ?? '-'}
                </S.Tabs.TabCell>

                <S.Tabs.TabCell data-label="Editado por">
                  {elm.usuarioEdicao ?? '-'}
                </S.Tabs.TabCell>

                <S.Tabs.TabCell data-label="Data da edição">
                  {formatTimeStampToDate(elm.data)}
                </S.Tabs.TabCell>

                <S.Tabs.TabCell
                  data-label="Ação realizada"
                  className="expanded-xl"
                >
                  {returnUserLog(elm ?? '-')}
                </S.Tabs.TabCell>
              </S.Tabs.TabBody>
            );
          })}
        </S.Tabs.TabsContainer>

        <S.ListContainer>
          {isLoading && <Loading />}
          {usersLogs.map((elm: any) => {
            return (
              <LogListItem key={elm.id} log={elm} type="user">
                <S.IconButton
                  className={'edit'}
                  onClick={() => handleOpenDetails(elm)}
                >
                  <img src={Details} width={16} />
                  Detalhes
                </S.IconButton>
              </LogListItem>
            );
          })}
        </S.ListContainer>

        {!isLoading && usersLogs.length === 0 && (
          <S.Message>Nenhum resultado encontrado.</S.Message>
        )}

        {!isLoading && usersLogs.length !== 0 && (
          <Pagination
            currentPage={currentPage}
            paginationSize={pageSize}
            onPageChange={handlePageChange}
            totalItems={metadata.totalItems}
          />
        )}
      </S.Content>

      {/* Modal */}
      <>
        <ModalResponsive
          title={'Detalhes de Log'}
          isOpenModal={OpenDetailsModal}
          handleOpenModal={setOpenDetailsModal}
        >
          <S.Featured>
            <S.Text>{returnUserLog(activeUser)}</S.Text>
          </S.Featured>
          <S.Buttons>
            <S.IconButton
              className="close"
              onClick={() => setOpenDetailsModal(false)}
            >
              Fechar
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>
      </>

      <ToastContainer
        closeButton={false}
        transition={Slide}
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </S.Container>
  );
};
